/**
 * Utility functions to extract batch and date from oncology submission filenames.
 *
 * Supported filename formats:
 * 1. New format: ASTERA_SinglePA_202512171628 (3 parts, timestamp at index 2)
 * 2. Old format: SOMETHING_SOMETHING_SOMETHING_YYYYMMDDhhmm (4+ parts, timestamp at index 3)
 * 3. Legacy format: SOMETHING_SOMETHING_SOMETHING_YYYYMMDD_hhmm (5 parts, date at index 3, time at index 4)
 */

/**
 * Extracts the raw date string (YYYYMMDD) from an oncology filename.
 * Handles multiple filename formats.
 */
export const extractOncologyDateFromFileName = (fileName: string): string => {
  if (!fileName || fileName === "--") {
    return "--";
  }

  try {
    const parts = fileName.split("_");

    // Format 1: ASTERA_SinglePA_202512171628 (3 parts)
    // Timestamp is at index 2
    if (parts.length === 3) {
      const timestampPart = parts[2];

      // Check if it's 12 digits (YYYYMMDDhhmm format)
      if (/^\d{12}/.test(timestampPart)) {
        return timestampPart.substring(0, 8); // Extract YYYYMMDD
      }

      // Check if it's 8 digits (YYYYMMDD format)
      if (/^\d{8}$/.test(timestampPart)) {
        return timestampPart;
      }
    }

    // Format 2: SOMETHING_SOMETHING_SOMETHING_YYYYMMDDhhmm (4+ parts)
    // Timestamp is at index 3
    if (parts.length >= 4) {
      const timestampPart = parts[3];

      // Check if it's 12 digits (YYYYMMDDhhmm format)
      if (/^\d{12}/.test(timestampPart)) {
        return timestampPart.substring(0, 8);
      }

      // Check if it's 8 digits (YYYYMMDD format)
      if (/^\d{8}$/.test(timestampPart)) {
        return timestampPart;
      }
    }

    return "--";
  } catch (error) {
    console.error("Error extracting date from oncology filename:", error);
    return "--";
  }
};

/**
 * Extracts and formats the date from an oncology filename as MM/DD/YYYY.
 */
export const extractOncologyFormattedDateFromFileName = (
  fileName: string,
): string => {
  const dateString = extractOncologyDateFromFileName(fileName);

  if (dateString === "--") {
    return "--";
  }

  try {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error("Error formatting date from oncology filename:", error);
    return "--";
  }
};

/**
 * Extracts the batch identifier from an oncology filename.
 * Returns format: YYYYMMDD_hhmm
 */
export const extractOncologyBatchFromFileName = (fileName: string): string => {
  if (!fileName || fileName === "--") {
    return "--";
  }

  try {
    const parts = fileName.split("_");

    // Format 1: ASTERA_SinglePA_202512171628 (3 parts)
    // Timestamp is at index 2
    if (parts.length === 3) {
      const timestampPart = parts[2];

      // Check if it's 12 digits (YYYYMMDDhhmm format)
      if (/^\d{12}/.test(timestampPart)) {
        const datePart = timestampPart.substring(0, 8); // YYYYMMDD
        const timePart = timestampPart.substring(8, 12); // hhmm
        return `${datePart}_${timePart}`;
      }

      // Check if it's 8 digits only (YYYYMMDD format) - no time available
      if (/^\d{8}$/.test(timestampPart)) {
        return timestampPart;
      }
    }

    // Format 2: SOMETHING_SOMETHING_SOMETHING_YYYYMMDDhhmm (4+ parts)
    // Timestamp is at index 3
    if (parts.length >= 4) {
      const timestampPart = parts[3];

      // Check if it's 12 digits (YYYYMMDDhhmm format)
      if (/^\d{12}/.test(timestampPart)) {
        const datePart = timestampPart.substring(0, 8);
        const timePart = timestampPart.substring(8, 12);
        return `${datePart}_${timePart}`;
      }

      // Format 3: Legacy format with separate date and time parts
      // SOMETHING_SOMETHING_SOMETHING_YYYYMMDD_hhmm
      if (parts.length >= 5) {
        const datePart = parts[3];
        const timePart = parts[4].replace(".csv", "");

        if (/^\d{8}$/.test(datePart) && /^\d{4}/.test(timePart)) {
          return `${datePart}_${timePart}`;
        }
      }
    }

    return "--";
  } catch (error) {
    console.error("Error extracting batch from oncology filename:", error);
    return "--";
  }
};
